import React from 'react';
import { Search, Sparkles, Monitor, Award, Shield, Package } from 'lucide-react';
import { useApp } from '../context/AppContext';

export const RefurbishmentPage = () => {
  const { t } = useApp();

  const steps = [
    {
      icon: Search,
      title: t('refurbishment.steps.inspection.title'),
      desc: t('refurbishment.steps.inspection.desc'),
      details: t('refurbishment.step1Items'),
    },
    {
      icon: Sparkles,
      title: t('refurbishment.steps.cleaning.title'),
      desc: t('refurbishment.steps.cleaning.desc'),
      details: t('refurbishment.step2Items'),
    },
    {
      icon: Monitor,
      title: t('refurbishment.steps.testing.title'),
      desc: t('refurbishment.steps.testing.desc'),
      details: t('refurbishment.step3Items'),
    },
    {
      icon: Award,
      title: t('refurbishment.steps.grading.title'),
      desc: t('refurbishment.steps.grading.desc'),
      details: t('refurbishment.step4Items'),
    },
    {
      icon: Shield,
      title: t('refurbishment.steps.dataWipe.title'),
      desc: t('refurbishment.steps.dataWipe.desc'),
      details: t('refurbishment.step5Items'),
    },
    {
      icon: Package,
      title: t('refurbishment.step6Title'),
      desc: t('refurbishment.step6Desc'),
      details: t('refurbishment.step6Items'),
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="refurbishment-page">
      {/* Hero */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00D4FF]/10 text-[#00D4FF] text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Professional Process
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-testid="refurbishment-title">
            {t('refurbishment.title')}
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            {t('refurbishment.subtitle')}
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative">
            {/* Connection Line */}
            <div className="absolute left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#00D4FF] via-[#00A0FF] to-transparent hidden lg:block" />

            <div className="space-y-12">
              {steps.map((step, idx) => (
                <div
                  key={idx}
                  className="relative flex flex-col lg:flex-row gap-8"
                  data-testid={`step-${idx + 1}`}
                >
                  {/* Step Number */}
                  <div className="flex-shrink-0 flex items-start gap-6">
                    <div className="w-16 h-16 flex items-center justify-center bg-[#0A0A0A] border border-[#00D4FF]/30 text-[#00D4FF] relative z-10">
                      <step.icon className="w-7 h-7" />
                    </div>
                    <div className="lg:hidden">
                      <div className="text-[#00D4FF] text-sm font-mono mb-1">{t('refurbishment.steps.step') || 'Step'} {idx + 1}</div>
                      <h3 className="text-xl font-semibold">{step.title}</h3>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="card-dark p-8 flex-1">
                    <div className="hidden lg:block">
                      <div className="text-[#00D4FF] text-sm font-mono mb-1">{t('refurbishment.steps.step') || 'Step'} {idx + 1}</div>
                      <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                    </div>
                    <p className="text-white/60 mb-6 lg:mt-0 mt-4">{step.desc}</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-white/70">
                          <div className="w-1.5 h-1.5 bg-[#00D4FF] rounded-full" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Promise */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">{t('refurbishment.qualityPromise')}</h2>
          <p className="text-white/70 text-lg leading-relaxed mb-8">
            {t('refurbishment.qualityPromiseText')} {t('refurbishment.qualityWarranty')}
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold gradient-text">100%</div>
              <div className="text-white/50 text-sm">{t('refurbishment.tested')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold gradient-text">90 Days</div>
              <div className="text-white/50 text-sm">{t('refurbishment.warranty')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold gradient-text">NIST</div>
              <div className="text-white/50 text-sm">{t('refurbishment.compliantWipe')}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
